import React, { useEffect, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { fetchLeagueHeatmap } from "../lib/api";

/**
 * LeagueHeatmap — PRO-league performance grid. Y axis = teams (most-matches
 * first), X axis = past N ISO weeks. Each cell is colour-coded by net
 * record (green for wins-heavy, red for losses-heavy, gray for no
 * matches).
 *
 * The endpoint returns 402 with a friendly detail when the league isn't
 * PRO — we surface that as the upgrade nudge.
 *
 * Props:
 *   leagueId: string
 *   weeks?: number (default 8, max 24)
 */
export function LeagueHeatmap({ leagueId, weeks = 8 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!leagueId) return undefined;
    let alive = true;
    setLoading(true); setError(null);
    fetchLeagueHeatmap(leagueId, weeks)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Couldn't load heatmap.");
        setLoading(false);
      });
    return () => { alive = false; };
  }, [leagueId, weeks]);

  if (loading) {
    return <p className="text-center text-white/55 py-8 text-sm">Loading heatmap…</p>;
  }
  if (error) {
    return (
      <div
        data-testid="heatmap-pro-gate"
        className="glass rounded-3xl border border-[#14b8a6]/40 p-6 text-center"
      >
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-crown mr-1" />PRO LEAGUE PERK
        </p>
        <p className="text-sm text-white/75 mt-2">{error}</p>
      </div>
    );
  }
  if (!data || (data.teams || []).length === 0) {
    return (
      <div data-testid="heatmap-empty" className="glass rounded-3xl p-6 text-center">
        <p className="text-sm text-white/55">
          No completed matches in the last {data?.weeks?.length ?? weeks} weeks yet.
        </p>
      </div>
    );
  }

  const cellByKey = new Map();
  for (const c of data.cells || []) {
    cellByKey.set(`${c.team_id}|${c.week}`, c);
  }

  const cellStyle = (cell) => {
    if (!cell || cell.matches === 0) {
      return { bg: "bg-white/5", color: "text-white/35", label: "—" };
    }
    const net = cell.net || 0;
    const intensity = Math.min(1, Math.abs(net) / 3);  // saturate at ±3
    if (net > 0) {
      return {
        bg: "",
        style: { background: `rgba(20, 184, 166, ${0.18 + intensity * 0.55})` },
        color: "text-[#0a1a0f]",
        label: `${cell.wins}-${cell.losses}`,
      };
    }
    if (net < 0) {
      return {
        bg: "",
        style: { background: `rgba(20, 184, 166, ${0.18 + intensity * 0.55})` },
        color: "text-[#1a0a0f]",
        label: `${cell.wins}-${cell.losses}`,
      };
    }
    return { bg: "bg-[#14b8a6]/30", color: "text-[#14b8a6]", label: `${cell.wins}-${cell.losses}` };
  };

  return (
    <div
      data-testid="league-heatmap"
      className="glass-strong rounded-3xl p-5 border border-[#14b8a6]/40 overflow-x-auto"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-3">
        <i className="fas fa-table-cells mr-1" />FORM HEATMAP · LAST {data.weeks.length} WEEKS
      </p>
      <table className="w-full min-w-[560px] text-[11px]">
        <thead>
          <tr className="text-white/55">
            <th className="text-left font-normal w-44">TEAM</th>
            {(data.weeks || []).map((w, i) => (
              <th key={i} className="text-center font-normal whitespace-nowrap px-1">
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.teams || []).slice(0, 12).map((t) => (
            <tr key={t.team_id} data-testid={`heatmap-row-${t.team_id}`} className="border-t border-white/5">
              <td className="py-1.5 pr-2">
                <div className="flex items-center gap-2 min-w-0">
                  <AvatarChip avatarId={t.avatar_id} pfpUrl={t.pfp_url} size={22} />
                  <span className="text-white truncate">{t.name}</span>
                  <span className="text-white/35 text-[10px] ml-auto">{t.totals}m</span>
                </div>
              </td>
              {(data.weeks || []).map((_w, idx) => {
                const cell = cellByKey.get(`${t.team_id}|${idx}`);
                const s = cellStyle(cell);
                return (
                  <td key={idx} className="px-0.5 py-0.5 text-center">
                    <div
                      className={`mx-auto rounded-md w-12 h-7 flex items-center justify-center font-bold ${s.bg} ${s.color}`}
                      style={s.style}
                      title={cell ? `${cell.wins}W · ${cell.losses}L` : "no match"}
                    >
                      {s.label}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-white/40 mt-3">
        🟢 green = winning week · 🔴 red = losing week · 🟡 yellow = .500 split · — = no match
      </p>
    </div>
  );
}

export default LeagueHeatmap;
